import { environment } from '../../../environments/environment';
import { Translation } from '../Translations';
import { InfoGroupDefinition, InfoPage } from './info-content';

/**
 * The guide's content (#4894).
 *
 * <p>Editing the guide means editing this file — the components carry no prose. Spanish is written
 * alongside English rather than routed through translations.json: these are paragraphs, not UI labels,
 * and keeping both languages on the same line is what stops one drifting from the other.</p>
 *
 * <p>Steps 2 and 3 of the pre-invoice flow differ on all three branches. Only the Invoice branch was
 * specified; the two receipt branches are marked <c>pending</c> rather than guessed, so the page states
 * plainly that the text is not written yet instead of inventing a rule someone might follow.</p>
 */

export const INFO_GROUPS: InfoGroupDefinition[] = [
  { id: 'flows', label: { en: 'Flows', es: 'Flujos' } },
  { id: 'business', label: { en: 'Business', es: 'Negocio' } },
];

const ORDER_UI = environment.domainUrl.order;

const PRE_INVOICE_FLOW: InfoPage = {
  slug: 'pre-invoice-flow',
  group: 'flows',
  navLabel: { en: 'Pre-invoice flow', es: 'Flujo de prefactura' },
  title: { en: 'The pre-invoice flow', es: 'El flujo de prefactura' },
  subtitle: {
    en: 'Pre-invoice is a flow, not a document type. It opens by asking what the document is based on, and that answer decides which AP document gets created.',
    es: 'La prefactura es un flujo, no un tipo de documento. Al abrirse pregunta en qué se basa el documento, y esa respuesta decide qué documento de CxP se crea.',
  },
  breadcrumb: [
    { en: 'Finance', es: 'Finanzas' },
    { en: 'Info', es: 'Información' },
  ],
  branches: [
    {
      id: 'invoice',
      label: { en: 'Based on an Invoice', es: 'Basado en una Factura' },
      caption: { en: 'Raw-material PO → creates Invoice', es: 'OC de materia prima → crea Factura' },
      description: {
        en: 'The invoice comes from a raw-material purchase order and was already created by someone else. Picking it here creates an AP document of type Invoice. Everything carries over and autopopulates from the source document, so the officer only changes the CAI, the GL account, the fees, and whether the service withholding is waived.',
        es: 'La factura proviene de una orden de compra de materia prima y ya fue creada por otra persona. Seleccionarla aquí crea un documento de CxP de tipo Factura. Todo se traslada y se autocompleta desde el documento origen, así que el oficial solo cambia el CAI, la cuenta contable, los cargos y si se exonera la retención por servicios.',
      },
      role: { en: 'AP Clerk — posting by AP Supervisor', es: 'Auxiliar de CxP — contabiliza el Supervisor de CxP' },
      link: { label: { en: 'Go to AP Documents', es: 'Ir a Documentos de CxP' }, route: '/ap-documents' },
    },
    {
      id: 'receipt-fiscal',
      label: { en: 'Based on a Receipt — fiscal', es: 'Basado en un Recibo — fiscal' },
      caption: { en: 'No PO, already has a fiscal number → creates Receipt', es: 'Sin OC, ya tiene número fiscal → crea Recibo' },
      description: {
        en: 'An independent document for something bought without a purchase order, where the provider already issued a fiscal number. Picking it here creates an AP document of type Receipt.',
        es: 'Un documento independiente por algo comprado sin orden de compra, donde el proveedor ya emitió un número fiscal. Seleccionarlo aquí crea un documento de CxP de tipo Recibo.',
      },
      link: { label: { en: 'Go to AP Documents', es: 'Ir a Documentos de CxP' }, route: '/ap-documents' },
    },
    {
      id: 'receipt-purchase',
      label: { en: 'Based on a Receipt — purchase', es: 'Basado en un Recibo — compra' },
      caption: { en: 'No PO, no fiscal number → creates PurchaseReceipt', es: 'Sin OC, sin número fiscal → crea Recibo de Compra' },
      description: {
        en: 'An independent document for something bought without a purchase order from a provider who issues no fiscal number. Picking it here creates an AP document of type PurchaseReceipt, and the fiscal number is generated from our own CAI.',
        es: 'Un documento independiente por algo comprado sin orden de compra a un proveedor que no emite número fiscal. Seleccionarlo aquí crea un documento de CxP de tipo Recibo de Compra, y el número fiscal se genera de nuestro propio CAI.',
      },
      link: { label: { en: 'Go to AP Documents', es: 'Ir a Documentos de CxP' }, route: '/ap-documents' },
    },
  ],
  steps: [
    {
      id: 'open',
      title: { en: 'Open the pre-invoice flow', es: 'Abrir el flujo de prefactura' },
      body: {
        en: 'Pre-invoice is a flow, not a document type. When it opens, the officer is prompted for what the document is based on. That answer is the only branch in the whole flow — everything after it follows from it.',
        es: 'La prefactura es un flujo, no un tipo de documento. Al abrirse, se le pregunta al oficial en qué se basa el documento. Esa respuesta es la única bifurcación de todo el flujo: todo lo demás se deriva de ella.',
      },
      bullets: [
        {
          term: { en: 'Based on an Invoice', es: 'Basado en una Factura' },
          description: {
            en: 'Comes from a raw-material PO and was already created by someone else.',
            es: 'Proviene de una OC de materia prima y ya fue creada por otra persona.',
          },
        },
        {
          term: { en: 'Based on a Receipt', es: 'Basado en un Recibo' },
          description: {
            en: 'An independent document for anything bought without a PO. Fiscal or purchase receipt.',
            es: 'Un documento independiente por cualquier compra sin OC. Recibo fiscal o de compra.',
          },
        },
        {
          term: { en: 'The flow builds the AP document', es: 'El flujo construye el documento de CxP' },
          description: {
            en: 'The officer never picks the AP document type by hand; the prompt decides it.',
            es: 'El oficial nunca elige el tipo de documento de CxP a mano; la pregunta lo decide.',
          },
        },
      ],
      conditions: [
        {
          when: { en: 'If the base is an Invoice', es: 'Si la base es una Factura' },
          then: { en: 'an AP document of type Invoice is created.', es: 'se crea un documento de CxP de tipo Factura.' },
        },
        {
          when: { en: 'If the base is a Receipt of type fiscal', es: 'Si la base es un Recibo de tipo fiscal' },
          then: { en: 'an AP document of type Receipt is created.', es: 'se crea un documento de CxP de tipo Recibo.' },
        },
        {
          when: { en: 'If the base is a Receipt of type purchase receipt', es: 'Si la base es un Recibo de tipo recibo de compra' },
          then: {
            en: 'an AP document of type PurchaseReceipt is created.',
            es: 'se crea un documento de CxP de tipo Recibo de Compra.',
          },
        },
      ],
    },
    {
      id: 'source-document',
      title: { en: 'Bring the source document', es: 'Traer el documento origen' },
      variants: {
        invoice: {
          body: {
            en: 'The invoice already exists against the raw-material PO. Everything carries over and autopopulates from it, so the officer only reviews.',
            es: 'La factura ya existe contra la OC de materia prima. Todo se traslada y se autocompleta desde ella, así que el oficial solo revisa.',
          },
          bullets: [
            {
              term: { en: 'PO of raw material', es: 'OC de materia prima' },
              description: {
                en: 'Approved and in status Received. Blanket and service POs are not accepted here.',
                es: 'Aprobada y en estado Recibida. No se aceptan OC abiertas ni de servicios.',
              },
            },
            {
              term: { en: 'Invoice created by someone else', es: 'Factura creada por otra persona' },
              description: { en: 'The officer picks it, does not create it.', es: 'El oficial la selecciona, no la crea.' },
            },
            {
              term: { en: 'Everything else is read-only', es: 'Todo lo demás es de solo lectura' },
              description: {
                en: 'Only the CAI, the GL account, the fees and the service-withholding waiver can be changed.',
                es: 'Solo se pueden cambiar el CAI, la cuenta contable, los cargos y la exoneración de retención por servicios.',
              },
            },
          ],
          links: [
            {
              label: { en: 'Order module — Purchase Orders', es: 'Módulo de Órdenes — Órdenes de Compra' },
              externalUrl: `${ORDER_UI}/purchase-orders`,
            },
            { label: { en: 'Order module — Receipts', es: 'Módulo de Órdenes — Recibos' }, externalUrl: `${ORDER_UI}/receipts` },
          ],
          conditions: [
            {
              when: { en: 'If no receipt exists yet', es: 'Si aún no existe el recibo' },
              then: {
                en: 'the document can only be saved as draft; posting is blocked until the receipt is registered.',
                es: 'el documento solo puede guardarse como borrador; la contabilización se bloquea hasta registrar el recibo.',
              },
            },
          ],
        },
        'receipt-fiscal': { pending: true },
        'receipt-purchase': { pending: true },
      },
    },
    {
      id: 'fiscal-number',
      title: { en: 'Fiscal number and CAI', es: 'Número fiscal y CAI' },
      variants: {
        invoice: {
          body: {
            en: 'The invoice arrives with its fiscal number. The officer selects the CAI it belongs to; the number itself is not editable.',
            es: 'La factura llega con su número fiscal. El oficial selecciona el CAI al que pertenece; el número en sí no es editable.',
          },
          bullets: [
            {
              term: { en: 'Fiscal number', es: 'Número fiscal' },
              description: { en: 'Carried over from the source invoice.', es: 'Trasladado desde la factura origen.' },
            },
            {
              term: { en: 'CAI', es: 'CAI' },
              description: {
                en: 'Selected by the officer, validated against the number range.',
                es: 'Seleccionado por el oficial, validado contra el rango de números.',
              },
            },
          ],
        },
        'receipt-fiscal': { pending: true },
        'receipt-purchase': { pending: true },
      },
    },
    {
      id: 'header',
      title: { en: 'Fill the header', es: 'Llenar el encabezado' },
      body: {
        en: 'The header is the same on all three branches: provider, dates, currency and reference. It is autopopulated from the source document.',
        es: 'El encabezado es igual en las tres ramas: proveedor, fechas, moneda y referencia. Se autocompleta desde el documento origen.',
      },
      bullets: [
        {
          term: { en: 'Provider', es: 'Proveedor' },
          description: {
            en: 'Sets payment terms, default GL accounts and the tax profile.',
            es: 'Define las condiciones de pago, las cuentas contables por defecto y el perfil fiscal.',
          },
        },
        {
          term: { en: 'Document date and posting date', es: 'Fecha de documento y fecha contable' },
          description: { en: 'Posting date drives the accounting period.', es: 'La fecha contable determina el período contable.' },
        },
        {
          term: { en: 'Currency and rate', es: 'Moneda y tasa' },
          description: {
            en: 'USD documents carry the rate used for the HNL value.',
            es: 'Los documentos en USD llevan la tasa usada para el valor en HNL.',
          },
        },
      ],
      conditions: [
        {
          when: { en: 'If the provider is domiciled in Honduras', es: 'Si el proveedor está domiciliado en Honduras' },
          then: {
            en: 'CAI and the fiscal number range are required, and the number is validated against the range. If the provider has none set, open the provider record to add it.',
            es: 'El CAI y el rango de números fiscales son obligatorios, y el número se valida contra el rango. Si el proveedor no tiene ninguno, abra el registro del proveedor para agregarlo.',
          },
          link: { label: { en: 'Business Provider Details', es: 'Detalles de Proveedor' }, route: '/businesses' },
        },
        {
          when: { en: 'If the provider is foreign', es: 'Si el proveedor es extranjero' },
          then: {
            en: 'no CAI is requested; the import declaration number is asked instead.',
            es: 'no se solicita CAI; en su lugar se pide el número de declaración de importación.',
          },
        },
        {
          when: { en: 'If the base is a purchase receipt', es: 'Si la base es un recibo de compra' },
          then: {
            en: 'the fiscal number is generated from our CAI instead of captured from the provider.',
            es: 'el número fiscal se genera de nuestro CAI en lugar de capturarse del proveedor.',
          },
        },
      ],
    },
    {
      id: 'lines',
      title: { en: 'Build the lines', es: 'Construir las líneas' },
      body: {
        en: 'Lines carry over from the source document on every branch. The GL account, the fees and the service-withholding waiver are the only things the officer changes.',
        es: 'Las líneas se trasladan del documento origen en todas las ramas. La cuenta contable, los cargos y la exoneración de retención por servicios son lo único que cambia el oficial.',
      },
      bullets: [
        {
          term: { en: 'GL account per line', es: 'Cuenta contable por línea' },
          description: {
            en: 'Defaults from the provider or the item; override where the posting needs it.',
            es: 'Por defecto viene del proveedor o del ítem; se puede sobrescribir donde la contabilización lo requiera.',
          },
        },
        {
          term: { en: 'Description, quantity, unit price', es: 'Descripción, cantidad, precio unitario' },
          description: {
            en: 'Quantities lock to the receipt where one exists.',
            es: 'Las cantidades se fijan al recibo cuando este existe.',
          },
        },
        {
          term: { en: 'Cost centre', es: 'Centro de costo' },
          description: {
            en: 'Required on expense lines, inherited on material lines.',
            es: 'Obligatorio en líneas de gasto, heredado en líneas de material.',
          },
        },
      ],
      conditions: [
        {
          when: { en: 'If the line is a service', es: 'Si la línea es un servicio' },
          then: {
            en: 'a service GL account is required, and the ISR retention check runs on the provider.',
            es: 'se requiere una cuenta contable de servicios, y se ejecuta la verificación de retención de ISR sobre el proveedor.',
          },
        },
        {
          when: { en: 'If the line is raw material', es: 'Si la línea es materia prima' },
          then: {
            en: 'the inventory GL is used and the line is not tied to a lot.',
            es: 'se usa la cuenta contable de inventario y la línea no se ata a un lote.',
          },
        },
        {
          when: { en: 'If the base is an Invoice', es: 'Si la base es una Factura' },
          then: {
            en: 'lines carry over from the PO and its receipt; variance above tolerance blocks posting.',
            es: 'las líneas se trasladan de la OC y su recibo; una variación por encima de la tolerancia bloquea la contabilización.',
          },
        },
        {
          when: { en: 'If a line has no item master', es: 'Si una línea no tiene maestro de ítem' },
          then: {
            en: 'the classification is taken from the receipt line, and that is what decides whether withholding applies.',
            es: 'la clasificación se toma de la línea del recibo, y eso es lo que decide si aplica la retención.',
          },
        },
        {
          when: { en: 'If a line has an item master', es: 'Si una línea tiene maestro de ítem' },
          then: {
            en: 'the classification is taken from the item master, and that is what decides whether withholding applies.',
            es: 'la clasificación se toma del maestro de ítem, y eso es lo que decide si aplica la retención.',
          },
        },
      ],
    },
    {
      id: 'taxes',
      title: { en: 'Taxes and withholding', es: 'Impuestos y retenciones' },
      body: {
        en: 'Tax is calculated on the lines once they are coded. The one thing the officer decides here is whether the service withholding is waived.',
        es: 'El impuesto se calcula sobre las líneas una vez codificadas. Lo único que decide el oficial aquí es si se exonera la retención por servicios.',
      },
      bullets: [
        {
          term: { en: 'ISV', es: 'ISV' },
          description: {
            en: 'Applied per line at the rate on the item or service.',
            es: 'Se aplica por línea a la tasa del ítem o servicio.',
          },
        },
        {
          term: { en: 'ISR retention', es: 'Retención de ISR' },
          description: {
            en: 'Calculated on service lines from the provider tax profile.',
            es: 'Se calcula sobre las líneas de servicio según el perfil fiscal del proveedor.',
          },
        },
        {
          term: { en: 'Waive service withholding', es: 'Exonerar retención por servicios' },
          description: { en: 'The officer can waive it on this document.', es: 'El oficial puede exonerarla en este documento.' },
        },
      ],
      conditions: [
        {
          when: { en: 'If the provider is exempt', es: 'Si el proveedor es exento' },
          then: {
            en: 'ISV is zero and the exemption number is recorded on the document.',
            es: 'el ISV es cero y el número de exoneración se registra en el documento.',
          },
        },
      ],
    },
    {
      id: 'post',
      title: { en: 'Review and post', es: 'Revisar y contabilizar' },
      body: {
        en: 'Same close on all three branches. The document is validated, numbered and sent to the ledger.',
        es: 'El cierre es igual en las tres ramas. El documento se valida, se numera y se envía al libro mayor.',
      },
      bullets: [
        {
          term: { en: 'Attachments', es: 'Adjuntos' },
          description: {
            en: 'Provider document, receipt and any approval, attached before posting.',
            es: 'Documento del proveedor, recibo y cualquier aprobación, adjuntos antes de contabilizar.',
          },
        },
        {
          term: { en: 'Balanced entry', es: 'Partida balanceada' },
          description: {
            en: 'Debits and credits must match before Post is enabled.',
            es: 'Débitos y créditos deben coincidir antes de habilitar Contabilizar.',
          },
        },
        {
          term: { en: 'Nexus number assigned', es: 'Número Nexus asignado' },
          description: {
            en: 'Internal number on posting; the fiscal number stays as captured.',
            es: 'Número interno al contabilizar; el número fiscal se mantiene tal como se capturó.',
          },
        },
      ],
      conditions: [
        {
          when: { en: 'If the base is a purchase receipt', es: 'Si la base es un recibo de compra' },
          then: {
            en: 'the fiscal number we generated is the one printed on the posted document.',
            es: 'el número fiscal que generamos es el que se imprime en el documento contabilizado.',
          },
        },
        {
          when: { en: 'If the document exceeds the approval threshold', es: 'Si el documento excede el umbral de aprobación' },
          then: {
            en: 'it goes to the Finance Manager queue instead of posting directly.',
            es: 'pasa a la cola del Gerente Financiero en lugar de contabilizarse directamente.',
          },
        },
      ],
    },
  ],
};

const TAX_AUTHORIZATION: InfoPage = {
  slug: 'tax-authorization',
  group: 'business',
  navLabel: { en: 'Tax Authorization', es: 'Autorización Fiscal' },
  parentSlug: 'provider-details',
  title: { en: 'Tax Authorization', es: 'Autorización Fiscal' },
  subtitle: {
    en: 'The CAI and fiscal number range registered on a provider, what documents each one covers, and how long it stays valid.',
    es: 'El CAI y el rango de números fiscales registrados en un proveedor, qué documentos cubre cada uno y por cuánto tiempo es válido.',
  },
  breadcrumb: [
    { en: 'Business', es: 'Negocio' },
    { en: 'Provider Details', es: 'Detalles de Proveedor' },
  ],
  body: {
    en: 'A tax authorization is issued per provider and per document type. It carries the CAI, the range of fiscal numbers it authorizes, and the validity dates. Finance validates every fiscal number captured on an AP document against the range, and generates numbers from our own range on a purchase receipt.',
    es: 'Una autorización fiscal se emite por proveedor y por tipo de documento. Lleva el CAI, el rango de números fiscales que autoriza y las fechas de vigencia. Finanzas valida contra el rango cada número fiscal capturado en un documento de CxP, y genera números de nuestro propio rango en un recibo de compra.',
  },
  links: [{ label: { en: 'Go to Provider Details', es: 'Ir a Detalles de Proveedor' }, route: '/businesses' }],
};

/**
 * Pages in sidebar order. `provider-details` is referenced as a parent by Tax Authorization but is not a
 * page of its own — it is a screen in the app, so the sidebar shows it as a heading only.
 */
export const INFO_PAGES: InfoPage[] = [PRE_INVOICE_FLOW, TAX_AUTHORIZATION];

/** Sidebar headings that group pages but have no article of their own. */
export const INFO_PARENT_LABELS: Record<string, Translation> = {
  'provider-details': { en: 'Provider Details', es: 'Detalles de Proveedor' },
};

export const DEFAULT_INFO_SLUG = PRE_INVOICE_FLOW.slug;
